import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/apiClient';
import Navbar from '../components/Navbar';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid,
} from 'recharts';

/**
 * Socratic Pathways — faculty-facing analytics for the multi-turn
 * dialogue trajectories students walk with the AI Tutor.  This is the
 * data foundation referenced by Patent A, Claim 21 (Pathway Cache):
 * each "pathway" is one chat session bundled into an ordered chain
 * with quality + outcome signals derived from the message log.
 */

const OUTCOME_COLOR = {
  resolved: '#16A34A',
  in_progress: '#0891B2',
  long_running: '#F59E0B',
  abandoned: '#DC2626',
};
const NAVY = '#1A2B4C';
const GOLD = '#FCD359';
const GOLD_DARK = '#E0B73D';

const fmtSec = (s) => {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined,
      { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(iso); }
};

// ─── Single pathway transcript view ────────────────────────────────────────
const PathwayDetail = ({ pathwayId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState('');
  const [annotating, setAnnotating] = useState(false);
  const [annotation, setAnnotation] = useState(null);   // {exemplary, notes, annotated_by, created_at}
  const [draftNotes, setDraftNotes] = useState('');

  useEffect(() => {
    let cancel = false;
    api.get(`/api/admin/pathways/${encodeURIComponent(pathwayId)}`)
      .then((r) => {
        if (!cancel) {
          setDetail(r.data);
          if (r.data?.annotation) setAnnotation(r.data.annotation);
        }
      })
      .catch((e) => { if (!cancel) setErr(e.response?.data?.detail || 'Failed to load pathway'); });
    return () => { cancel = true; };
  }, [pathwayId]);

  const submitAnnotation = async (exemplary) => {
    setAnnotating(true);
    try {
      const r = await api.post('/api/admin/log-pathway', {
        pathway_id: pathwayId,
        exemplary,
        notes: draftNotes || '',
      });
      setAnnotation(r.data?.annotation || { exemplary, notes: draftNotes });
      setDraftNotes('');
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.response?.data?.detail || 'Failed to save annotation');
    } finally {
      setAnnotating(false);
    }
  };

  if (err) return (
    <div className="card p-6 text-red-600" data-testid="pathway-detail-error">{err}</div>
  );
  if (!detail) return (
    <div className="card p-6 text-gray-500" data-testid="pathway-detail-loading">Loading transcript…</div>
  );

  const s = detail.summary || {};
  return (
    <div data-testid="pathway-detail">
      <button onClick={onClose}
              className="text-sm font-semibold mb-3"
              style={{ color: NAVY }}
              data-testid="pathway-back-btn">
        ← Back to pathway list
      </button>

      <div className="card p-5 mb-4">
        <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: GOLD_DARK }}>
          Pathway transcript
        </div>
        <div className="text-lg font-bold mb-3" style={{ color: NAVY }}>{s.goal || '—'}</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
          <Stat label="Turns" value={s.turns_count ?? detail.turns.length} />
          <Stat label="Outcome" value={s.outcome || 'unknown'} colour={OUTCOME_COLOR[s.outcome] || NAVY} />
          <Stat label="Quality" value={`${s.quality_score ?? '—'} / 100`} />
          <Stat label="Duration" value={fmtSec(s.duration_seconds)} />
          <Stat label="RAG turns" value={`${s.rag_turn_count ?? 0} / ${s.turns_count ?? 0}`} />
        </div>

        {/* ── Faculty annotation: ★ Exemplary / needs review ──────────── */}
        <div className="mt-4 pt-4 border-t"
             style={{ borderColor: 'var(--gr-border)' }}
             data-testid="pathway-annotation-block">
          {annotation ? (
            <div className="flex items-start gap-3 flex-wrap">
              <span className="text-xs uppercase tracking-wider font-bold"
                    style={{ color: GOLD_DARK }}>Faculty annotation</span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold"
                    style={{
                      background: annotation.exemplary ? 'rgba(252,211,89,0.22)' : 'var(--gr-taupe)',
                      color: NAVY,
                    }}
                    data-testid="pathway-annotation-chip">
                {annotation.exemplary ? '★ Exemplary' : 'Flagged'}
              </span>
              {annotation.notes && (
                <span className="text-sm text-gray-700 italic" data-testid="pathway-annotation-notes">
                  &ldquo;{annotation.notes}&rdquo;
                </span>
              )}
              <span className="text-[11px] text-gray-500 ml-auto">
                by {annotation.annotated_by || '—'}
                {annotation.created_at && ` · ${fmtDate(annotation.created_at)}`}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-wider font-bold mr-2"
                    style={{ color: GOLD_DARK }}>Annotate</span>
              <input
                type="text"
                value={draftNotes}
                onChange={(e) => setDraftNotes(e.target.value)}
                placeholder="Why is this pathway noteworthy? (optional)"
                maxLength={500}
                className="border rounded px-2 py-1 text-sm flex-1 min-w-[220px]"
                style={{ borderColor: 'var(--gr-border)' }}
                data-testid="pathway-annotation-notes-input"
              />
              <button
                disabled={annotating}
                onClick={() => submitAnnotation(true)}
                className="px-3 py-1.5 rounded text-sm font-bold disabled:opacity-50"
                style={{ background: 'var(--gr-gold)', color: NAVY }}
                data-testid="pathway-mark-exemplary-btn">
                ★ Mark Exemplary
              </button>
              <button
                disabled={annotating}
                onClick={() => submitAnnotation(false)}
                className="px-3 py-1.5 rounded text-sm font-bold border disabled:opacity-50"
                style={{ borderColor: NAVY, color: NAVY, background: 'white' }}
                data-testid="pathway-flag-review-btn">
                Flag for review
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card p-3" style={{ background: 'var(--gr-bone)' }}>
        {detail.turns.map((t, i) => (
          <div key={i}
               className="border-b last:border-b-0 py-3 px-2"
               style={{ borderColor: 'var(--gr-border)' }}
               data-testid={`pathway-turn-${i}`}>
            <div className="flex items-center gap-2 text-xs mb-1">
              <span className="font-bold uppercase tracking-wider"
                    style={{ color: t.role === 'user' ? GOLD_DARK : NAVY }}>
                {t.role === 'user' ? 'Student' : 'AI Tutor'}
              </span>
              <span className="text-gray-400">{fmtDate(t.created_at)}</span>
              {t.scope && (
                <span className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: 'var(--gr-taupe)', color: NAVY }}>
                  {t.scope}
                </span>
              )}
              {t.rag_used && (
                <span className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: 'rgba(252,211,89,0.22)', color: GOLD_DARK }}>
                  RAG · {t.rag_chunk_count || 0}
                </span>
              )}
              {t.cached && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                  cached
                </span>
              )}
              {t.streamed && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700">
                  streamed
                </span>
              )}
            </div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed text-gray-800">
              {t.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Stat = ({ label, value, colour }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className="text-base font-bold mt-0.5" style={{ color: colour || NAVY }}>
      {value}
    </div>
  </div>
);

// ─── Main page ─────────────────────────────────────────────────────────────
const PathwaysPage = () => {
  const { pathwayId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pathways, setPathways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (pathwayId) return; // detail mode handled by sub-component
    let cancel = false;
    const load = async () => {
      setLoading(true);
      try {
        const [statsRes, listRes] = await Promise.all([
          api.get(`/api/admin/pathways/stats?days=${days}`),
          api.get(`/api/admin/pathways?limit=100${outcomeFilter ? `&outcome=${outcomeFilter}` : ''}`),
        ]);
        if (cancel) return;
        setStats(statsRes.data);
        setPathways(listRes.data?.pathways || []);
      } catch (e) {
        if (!cancel) console.error('Pathways load failed', e);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [days, outcomeFilter, pathwayId]);

  const outcomePie = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.outcome_breakdown || {}).map(([name, value]) => ({ name, value }));
  }, [stats]);

  const lengthBars = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.length_buckets || {}).map(([bucket, count]) => ({ bucket, count }));
  }, [stats]);

  const scopeBars = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.scope_totals || {}).map(([scope, count]) => ({ scope, count }));
  }, [stats]);

  if (pathwayId) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--gr-bone-2)' }}>
        <Navbar />
        <main className="max-w-6xl mx-auto px-6 py-6">
          <PathwayDetail
            pathwayId={pathwayId}
            onClose={() => navigate('/pathways')}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--gr-bone-2)' }}>
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-6" data-testid="pathways-page">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: NAVY }} data-testid="pathways-title">
              Socratic Pathways
            </h1>
            <div className="text-sm text-gray-600 mt-1">
              Multi-turn dialogue trajectories captured from every AI Tutor session — the data
              foundation for the patented Pathway Cache (U.S. Prov. App. No. 64/084,230, Claim 21).
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Window</label>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="input-field py-1 px-2 text-sm"
              data-testid="pathways-window-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="card p-10 text-center text-gray-500" data-testid="pathways-loading">Loading pathways…</div>
        ) : !stats ? null : (
          <>
            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Kpi label="Total pathways" value={stats.total_pathways} accent={NAVY} />
              <Kpi label="Resolution rate" value={`${stats.resolution_rate}%`} accent={OUTCOME_COLOR.resolved} />
              <Kpi label="Avg turns / path" value={stats.avg_turns} accent={GOLD_DARK} />
              <Kpi label="Resolved" value={stats.resolved} accent={NAVY} />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <ChartCard title="Outcome breakdown">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={outcomePie} dataKey="value" nameKey="name"
                         cx="50%" cy="50%" outerRadius={70} label>
                      {outcomePie.map((entry) => (
                        <Cell key={entry.name} fill={OUTCOME_COLOR[entry.name] || NAVY} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={28} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Pathway length distribution">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={lengthBars}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill={GOLD_DARK} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Turns by scope">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={scopeBars}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="scope" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill={NAVY} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            {stats.daily_volume?.length > 1 && (
              <div className="mb-4">
                <ChartCard title="Daily pathway volume">
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={stats.daily_volume}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke={NAVY} strokeWidth={2}
                            dot={{ fill: GOLD, stroke: NAVY }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            )}

            {/* Top struggling topics */}
            {stats.top_struggling_topics?.length > 0 && (
              <div className="card p-4 mb-4" data-testid="struggling-topics-card">
                <div className="text-sm font-bold uppercase tracking-wider mb-3"
                     style={{ color: NAVY }}>
                  Top struggling topics
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                        <th className="pb-2">Topic</th>
                        <th className="pb-2">Module</th>
                        <th className="pb-2 text-right">Sessions</th>
                        <th className="pb-2 text-right">Avg turns</th>
                        <th className="pb-2 text-right">Abandon %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_struggling_topics.map((t) => (
                        <tr key={t.topic_id} className="border-t" style={{ borderColor: 'var(--gr-border)' }}>
                          <td className="py-2 font-mono text-xs">{t.topic_id}</td>
                          <td className="py-2 font-mono text-xs">{t.module_id || '—'}</td>
                          <td className="py-2 text-right">{t.count}</td>
                          <td className="py-2 text-right font-bold" style={{ color: NAVY }}>{t.avg_turns}</td>
                          <td className="py-2 text-right font-bold"
                              style={{ color: t.abandon_rate > 50 ? OUTCOME_COLOR.abandoned : NAVY }}>
                            {t.abandon_rate}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pathway list with filter */}
            <div className="card p-4" data-testid="pathways-list-card">
              <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
                <div className="text-sm font-bold uppercase tracking-wider" style={{ color: NAVY }}>
                  Recent pathways ({pathways.length})
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs uppercase tracking-wider text-gray-500 font-bold">Outcome</label>
                  <select
                    value={outcomeFilter}
                    onChange={(e) => setOutcomeFilter(e.target.value)}
                    className="input-field py-1 px-2 text-sm"
                    data-testid="pathways-outcome-filter"
                  >
                    <option value="">All</option>
                    <option value="resolved">Resolved</option>
                    <option value="in_progress">In progress</option>
                    <option value="long_running">Long running</option>
                    <option value="abandoned">Abandoned</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                      <th className="pb-2">Goal (first question)</th>
                      <th className="pb-2">Student</th>
                      <th className="pb-2 text-right">Turns</th>
                      <th className="pb-2">Outcome</th>
                      <th className="pb-2 text-right">Quality</th>
                      <th className="pb-2">Ended</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pathways.map((p) => (
                      <tr key={p.pathway_id}
                          className="border-t hover:bg-yellow-50 cursor-pointer"
                          style={{ borderColor: 'var(--gr-border)' }}
                          onClick={() => navigate(`/pathways/${encodeURIComponent(p.pathway_id)}`)}
                          data-testid={`pathway-row-${p.pathway_id}`}>
                        <td className="py-2 max-w-xs truncate" title={p.goal}>{p.goal || '—'}</td>
                        <td className="py-2 text-xs text-gray-500">{p.user_email}</td>
                        <td className="py-2 text-right font-bold" style={{ color: NAVY }}>{p.turns_count}</td>
                        <td className="py-2">
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded"
                                style={{
                                  background: `${OUTCOME_COLOR[p.outcome] || NAVY}1a`,
                                  color: OUTCOME_COLOR[p.outcome] || NAVY,
                                }}>
                            {p.outcome}
                          </span>
                        </td>
                        <td className="py-2 text-right font-bold" style={{ color: GOLD_DARK }}>
                          {p.quality_score}
                        </td>
                        <td className="py-2 text-xs text-gray-500">{fmtDate(p.ended_at)}</td>
                      </tr>
                    ))}
                    {pathways.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-6 text-center text-gray-500">
                          No pathways in this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const Kpi = ({ label, value, accent }) => (
  <div className="card p-4">
    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className="text-2xl font-bold mt-1" style={{ color: accent || NAVY }}>{value}</div>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="card p-4">
    <div className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: NAVY }}>
      {title}
    </div>
    {children}
  </div>
);

export default PathwaysPage;
